/* src/views/logements/FicheDocsCtrl.jsx — L'ONGLET « Documents » de la
   fiche logement (lot 4, 2026-08-28) : trois vues du proto.

   · LE CONTRAT — les valeurs que CE logement apporte (avec les manques en
     rouge et le décompte honnête), le texte des articles en FEUILLE
     PROVISOIRE (décision 5 : ContratTab existant monté tel quel — clauses,
     validation live, aperçu, envoi au voyageur DRY_RUN honnête — il
     rejoindra Réglages · Contrat).
   · LES REÇUS — le dessin refait par Dimitri (décision 1) : aperçu sans
     consommer, blocage préfixe manquant, prochain numéro annoncé. Depuis la
     décision 34 (30/10), la pièce est émise AUTOMATIQUEMENT au départ, une
     seule par séjour (api/cron/emit-receipts.js) : le bouton « Émettre » est
     retiré. Geste « Renvoyer » : ré-email du html_snapshot archivé, JAMAIS
     de nouveau numéro — par le serveur (`facture_lire` puis
     `facture_renvoyer`, lot 3c-3), jamais depuis le navigateur.
   · LE FORMULAIRE — l'allure, les champs (exigé/retirer/monter-descendre,
     l'honnêteté « exigé n'engage que le navigateur »), la pièce d'identité
     par canal, le lien envoyé + la feuille « Gérer les liens » (tokens :
     générer, copier, voir, révoquer — fonctions conservées). */

import { nomLogementFacture, logPourFacture } from "../../lib/nom-logement-facture"
import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '../../supabase'
import { resolveEmitter } from '../../lib/emitter-resolver'
import { resolveCheckinCheckout } from '../../lib/horaires'
import { resolveCautionFromLog } from '../../lib/caution-amount'
import { genFactureResaHTML } from '../../lib/facture-resa-html'
import { ouvrirApercuIsole } from '../../lib/apercu-isole'
import { docLabelForProp, invoiceTypeLabel, listInvoicesByEmitter,
  resolveInvoicePrefix } from '../../lib/invoices'
import { computePreviewNumber } from '../../lib/invoice-preview'
import { migrateLegacyToUnified, getIdDocPolicy } from '../../lib/forms-migration'
import { resolveCoverPhoto, uploadCoverPhoto, clearCoverPhotoPatch } from '../../lib/form-cover-photo'
import { createFormToken, listFormTokens, publicFormUrl, deleteFormToken } from '../../lib/forms-api'
import ContratTab from '../../tabs/logements/ContratTab'
import Feuille from '../accueil/Feuille'
import { ToggleRond } from './proto/LivretProto'

const fmtEuros = (n) => Number(n).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
const fmtDateCourt = (iso) => {
  if (!iso) return '—'
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`)
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' }).format(d)
}
const SOURCES_LB = { airbnb: 'Airbnb', booking: 'Booking', direct: 'Direct' }

/* ── la bascule des trois vues ────────────────────────────────────── */
function BasculeDocs({ actif, onVue, pointContrat, nbAEnvoyer }) {
  const vues = [
    { id: 'contrat', lb: 'Le contrat', point: pointContrat ? '#b8412b' : null },
    { id: 'recus', lb: 'Les reçus', point: nbAEnvoyer > 0 ? '#d98c1f' : null },
    { id: 'form', lb: 'Le formulaire', point: null },
  ]
  return (
    <div style={{ display: 'flex', gap: '3px', background: '#eef2f6',
      borderRadius: '10px', padding: '3px', marginBottom: '12px',
      width: 'fit-content', maxWidth: '100%', overflowX: 'auto',
      scrollbarWidth: 'none', touchAction: 'pan-x' }}>
      {vues.map((v) => (
        <button key={v.id} onClick={() => onVue(v.id)}
          data-testid={`docs-vue-${v.id}`}
          style={actif === v.id
            ? { display: 'inline-flex', alignItems: 'center', gap: '7px',
              minHeight: '36px', padding: '0 13px', borderRadius: '8px',
              fontSize: '12px', whiteSpace: 'nowrap', background: '#fff',
              color: 'var(--navy-d)', fontWeight: '600', border: 'none',
              boxShadow: '0 1px 3px rgba(35,50,64,.12)', cursor: 'pointer' }
            : { display: 'inline-flex', alignItems: 'center', gap: '7px',
              minHeight: '36px', padding: '0 13px', borderRadius: '8px',
              fontSize: '12px', whiteSpace: 'nowrap', color: 'var(--gris)',
              background: 'transparent', border: 'none', cursor: 'pointer' }}>
          {v.lb}{v.point && (
            <span style={{ width: '6px', height: '6px', borderRadius: '50%',
              background: v.point, flexShrink: '0' }} />
          )}
        </button>
      ))}
    </div>
  )
}

export default function FicheDocsCtrl({ draft }) {
  const { selected, c } = draft
  const log = selected.entity
  const prop = selected.prop
  const parentLog = selected.parentLog
  const [vue, setVue] = useState('contrat')
  const [feuille, setFeuille] = useState(null)
  useEffect(() => { setVue('contrat'); setFeuille(null) }, [log.id])

  const recus = useRecus({ log, parentLog, prop, cfg: c })

  // Le contrat ne sert qu'au parcours direct : si la page du logement n'est
  // pas publiée, il ne partira jamais tout seul (décision 2026-08-28 — même
  // franchise que les textes automatiques). Lecture fail-soft.
  const [venteDirecte, setVenteDirecte] = useState(null)
  useEffect(() => {
    let vivant = true
    setVenteDirecte(null)
    supabase.from('logement_site_content')
      .select('statut_publication').eq('logement_id', log.id).maybeSingle()
      .then(({ data, error }) => {
        if (!vivant) return
        if (error) { console.error('[docs] statut publication', error.message); return }
        setVenteDirecte(data?.statut_publication === 'published')
      })
    return () => { vivant = false }
  }, [log.id])

  // ── les valeurs que ce logement apporte au contrat ────────────────
  const effectiveLog = parentLog ? { ...parentLog, ...log } : log
  const { checkin, checkout } = resolveCheckinCheckout(effectiveLog)
  // Règle unique (07/10) : 0 = pas de caution, vide = celle de la maison.
  const caution = resolveCautionFromLog(parentLog ? { ...log, parentLog } : log)
  const mediateurOk = !!(c?.co?.mediateur_nom && c?.co?.mediateur_coordonnees)
  const valeurs = [
    // Le nom PROPRE de la chambre (30/09, même règle que les factures) : celui
    // de la maison ne compte pas — le contrat ne l'imprimera jamais.
    { lb: 'Nom du logement', src: 'Instructions', v: nomLogementFacture(logPourFacture(log, parentLog)) },
    { lb: 'Adresse complète', src: 'Instructions', v: effectiveLog.adresse_complete || null },
    { lb: "Heures d'arrivée et de départ", src: 'Instructions',
      v: checkin && checkout ? `${checkin} → ${checkout}` : null },
    { lb: 'Montant de la caution', src: 'la fiche, bloc Caution',
      v: caution != null ? fmtEuros(caution) : null },
    { lb: 'Nombre de chambres', src: 'Instructions',
      v: effectiveLog.capacity?.bedrooms != null ? String(effectiveLog.capacity.bedrooms) : null },
    { lb: 'Nombre de salles de bain', src: 'Instructions',
      v: effectiveLog.capacity?.bathrooms != null ? String(effectiveLog.capacity.bathrooms) : null },
    { lb: 'Médiateur de la consommation', src: 'Réglages · Société',
      v: mediateurOk ? c.co.mediateur_nom : null },
  ]
  const nbManquants = valeurs.filter((x) => x.v == null).length
  const nomManque = valeurs[0].v == null

  return (
    <>
      <BasculeDocs actif={vue} onVue={setVue}
        pointContrat={nbManquants > 0}
        nbAEnvoyer={recus.sansRecu.length} />

      {/* ═══ LE CONTRAT ════════════════════════════════════════════ */}
      {vue === 'contrat' && (
        <>
          <div className="info">Le contrat ne sert qu'aux <b>réservations
            directes</b>. Sur Airbnb et Booking, c'est la plateforme qui le
            fournit.</div>
          {venteDirecte === false && (
            <div className="warn" data-testid="contrat-nulle-part">Ce contrat ne
              partira jamais tout seul : la page directe de ce logement n'est
              pas publiée, aucun parcours ne l'enverra. L'aperçu et l'envoi à
              la main restent possibles.</div>
          )}
          <button onClick={() => setFeuille('clauses')} data-testid="contrat-texte-articles"
            style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%',
              textAlign: 'left', background: '#f6f8fa', border: '1px solid var(--bord)',
              borderRadius: '12px', padding: '11px 12px', minHeight: '58px',
              marginBottom: '12px', cursor: 'pointer' }}>
            <span style={{ width: '28px', height: '28px', borderRadius: '8px',
              background: '#fff', border: '1px solid var(--bord)', color: '#5c6773',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', flexShrink: '0' }}>⚙</span>
            <span style={{ flex: '1', minWidth: '0' }}>
              <span style={{ display: 'block', fontSize: '12px', fontWeight: '600',
                color: 'var(--navy-d)' }}>Le texte des articles</span>
              <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
                marginTop: '1px' }}>écrit une fois, pour tous les logements —
                rejoindra Réglages · Contrat</span>
            </span>
            <span style={{ fontSize: '15px', color: 'var(--gris-d)',
              flexShrink: '0' }}>›</span>
          </button>
          <p className="inter" style={{ marginTop: '0' }}>Ce que ce logement
            apporte au contrat</p>
          <div className="carte" style={{ margin: '0' }}>
            {valeurs.map((x, i) => (
              <div key={x.lb} style={{ display: 'flex', alignItems: 'center',
                gap: '10px', padding: '9px 0',
                borderTop: i > 0 ? '1px solid var(--bord)' : 'none' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%',
                  flexShrink: '0', background: x.v != null ? '#2f9e6e' : '#b8412b' }} />
                <span style={{ flex: '1', minWidth: '0' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    color: 'var(--navy-d)' }}>{x.lb}</span>
                  <span style={{ display: 'block', fontSize: '9.5px',
                    color: 'var(--gris-d)', marginTop: '1px' }}>{x.src}</span>
                </span>
                <b style={{ fontSize: '11.5px',
                  color: x.v != null ? 'var(--navy-d)' : 'var(--terra-tx)',
                  flexShrink: '0', textAlign: 'right', maxWidth: '44%',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap' }}>{x.v != null ? x.v : 'manque'}</b>
              </div>
            ))}
          </div>
          {nbManquants > 0 && (
            <div className="dang">{nbManquants} valeur{nbManquants > 1 ? 's' : ''} manque{nbManquants > 1 ? 'nt' : ''}.
              {nomManque
                ? ' Sans nom du logement, le contrat ne sortira pas.'
                : ' Le contrat sortira quand même — avec un blanc à leur place.'}</div>
          )}
          <div className="act">
            <button className="bt bt-p" data-testid="contrat-voir"
              onClick={() => setFeuille('clauses')}>Voir le contrat de ce logement</button>
            <button className="bt bt-s" onClick={() => setFeuille('clauses')}>Modifier le texte</button>
          </div>
          <p className="note">Modifier le texte le change pour tous les
            logements. Ce qui est propre à celui-ci, ce sont les valeurs
            ci-dessus.</p>
        </>
      )}

      {/* ═══ LES REÇUS ═════════════════════════════════════════════ */}
      {vue === 'recus' && (
        <VueRecus recus={recus} />
      )}

      {/* ═══ LE FORMULAIRE ═════════════════════════════════════════ */}
      {vue === 'form' && (
        <VueFormulaire draft={draft} log={log}
          onGererLiens={() => setFeuille('liens')} />
      )}

      {/* ═══ les feuilles ══════════════════════════════════════════ */}
      {feuille === 'clauses' && (
        <Feuille titre="Le texte du contrat" suffixe="provisoire" large
          testId="feuille-contrat-clauses" onFermer={() => setFeuille(null)}
          enfants={(
            <>
              <div className="info">Provisoire : ce texte rejoindra{' '}
                <b>Réglages · Contrat</b> quand son lot arrivera. Les clauses
                s'enregistrent avec la pastille « Enregistrer » de la fiche.</div>
              {/* ContratTab existant monté tel quel : clauses + validation live
                  bloquante, clauses par défaut, sélecteur de résa, aperçu /
                  imprimer, envoi au voyageur (toast DRY_RUN honnête). */}
              <ContratTab log={log} parentLog={parentLog} prop={prop}
                propNom={prop?.nom} cfg={c} onUpdate={draft.updateLog} />
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Fermer</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'liens' && (
        <FeuilleLiens logementId={log.id}
          expiryDays={migrateLegacyToUnified(log.forms || {}).link_expiry_days ?? 14}
          onFermer={() => setFeuille(null)} />
      )}
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   LES REÇUS — mécanique FactureTab portée à l'identique + « Renvoyer ».
   ═══════════════════════════════════════════════════════════════════ */

/* La pièce VIVANTE de chaque réservation. Une pièce annulée ou un avoir n'en
   est pas une (lot 3c-3) : ni « Renvoyer », ni pastille de numéro pour eux —
   la porte serveur les refuserait de toute façon. */
export function indexerPiecesParResa(invs) {
  const byResa = {}
  for (const inv of invs || []) {
    if (!inv || !inv.reservation_id) continue
    if (!['invoice', 'receipt'].includes(inv.invoice_type)) continue
    if (inv.status === 'cancelled' || inv.cancelled_at) continue
    byResa[inv.reservation_id] = inv
  }
  return byResa
}

/* RENVOYER UNE PIÈCE — la porte serveur de l'opérateur, rien d'autre (lot
   3c-3, 08/10/2026). Avant : le navigateur envoyait lui-même adresse, objet
   et HTML par l'envoi générique de courriels (celui des contrats) — ni
   journal, ni anti-doublon, ni délai, et une pièce annulée pouvait partir.
   1. `facture_lire` : le serveur relit la pièce, le destinataire (masqué) et
      le journal des envois, et rend la phrase de confirmation + le jeton ;
   2. rien ne part sans le OUI de Dimitri sur CETTE phrase ;
   3. `facture_renvoyer` sous `x-apercu-version` : le serveur envoie le
      document archivé (clé anti-doublon, délai) et note le résultat.
   Ce que l'écran dit vient de la réponse du serveur — jamais « renvoyé »
   sans sa preuve. Rend { ok, annule?, message }. */
export async function renvoyerPiece({ invoiceId, token, confirmer, fetchImpl }) {
  const envoyerA = fetchImpl || ((...a) => fetch(...a))
  const appeler = (action, entetes = {}) => envoyerA(`/api/beds24-mapping?action=${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...entetes },
    body: JSON.stringify({ action, invoice_id: invoiceId }),
  })
  const lireJson = async (resp, quoi) => {
    try { return await resp.json() } catch (e) { console.error(`[recus] ${quoi} : réponse illisible`, e); return null }
  }

  // 1. La lecture : rien n'est parti tant qu'elle n'a pas répondu.
  let lu
  try {
    const resp = await appeler('facture_lire')
    lu = { httpOk: resp.ok, json: await lireJson(resp, 'lecture') }
  } catch (e) {
    console.error('[recus] lecture avant renvoi', e)
    return { ok: false, message: "Lecture de la pièce impossible : rien n'est parti. Réessaie." }
  }
  const j1 = lu.json
  if (!lu.httpOk || !j1 || j1.ok !== true || typeof j1.cfg_version !== 'string' || !j1.cfg_version) {
    return { ok: false, message: (j1 && j1.error) || "Lecture de la pièce impossible : rien n'est parti." }
  }

  // 2. La confirmation, avec la phrase du serveur.
  const phrase = j1.confirmation || j1.actuel_lisible || `Renvoyer ${j1.numero || 'cette pièce'} ?`
  if (!confirmer(phrase)) return { ok: false, annule: true, message: null }

  // 3. Le renvoi.
  let resp
  try {
    resp = await appeler('facture_renvoyer', { 'x-apercu-version': j1.cfg_version })
  } catch (e) {
    console.error('[recus] renvoi : réponse perdue', e)
    return { ok: false, message: 'Réponse perdue : impossible de savoir si le courriel est parti. Vérifie avec le voyageur avant de réessayer.' }
  }
  const j2 = await lireJson(resp, 'renvoi')
  if (!j2) {
    return { ok: false, message: 'Réponse illisible : impossible de savoir si le courriel est parti. Vérifie avec le voyageur avant de réessayer.' }
  }
  if (resp.ok && j2.ok === true && typeof j2.resume === 'string' && j2.resume) {
    return { ok: true, message: `${j2.dry_run ? '🧪' : '✅'} ${j2.resume}` }
  }
  if (j2.code === 'apercu_perime') {
    return { ok: false, message: "La pièce ou son journal d'envoi a changé depuis la lecture : rien n'est parti. Clique de nouveau sur « Renvoyer » pour relire." }
  }
  return { ok: false, message: j2.message || j2.error || `Renvoi non confirmé (HTTP ${resp.status}) : impossible de savoir si le courriel est parti.` }
}

function useRecus({ log, parentLog, prop, cfg }) {
  // Le nom figé sur la pièce vient de la CHAMBRE seule (29/09) — adresse et
  // contact restent hérités de la maison.
  const effectiveLog = useMemo(() => logPourFacture(log, parentLog), [parentLog, log])
  const emitter = resolveEmitter({ cfg, prop })
  const tabDocLabel = docLabelForProp(prop)
  const tabDocLabelLow = docLabelForProp(prop, { lower: true })
  let invoicePrefix = null
  let prefixError = null
  try {
    invoicePrefix = resolveInvoicePrefix({ emitter, prop, docType: 'voyageur' })
  } catch (e) {
    prefixError = 'Préfixe de facturation manquant pour ce propriétaire — à renseigner dans Infos · Facturation propriétaire.'
  }

  const [resas, setResas] = useState([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)
  const [invoicesByResa, setInvoicesByResa] = useState({})
  const [sequences, setSequences] = useState(null)
  useEffect(() => {
    let mounted = true
    setLoading(true); setInfo(null); setError(null)
    supabase.from('reservations')
      .select('id, guest_first_name, guest_last_name, guest_email, guest_phone, guest_count, start_date, end_date, total_amount, cleaning_fee, city_tax, ota_commission, source, status, beds24_booking_id')
      .eq('logement_id', log.id)
      .order('start_date', { ascending: false })
      .limit(50)
      .then(async ({ data, error: e }) => {
        if (!mounted) return
        if (e) { setError(`Chargement résas : ${e.message}`); setLoading(false); return }
        setResas(data || [])
        try {
          // Anti-doublon CGI 289 : toutes les années couvertes par les résas.
          const resaYears = (data || [])
            .flatMap((r) => [r.start_date, r.end_date]).filter(Boolean)
            .map((d) => Number(String(d).slice(0, 4))).filter(Number.isFinite)
          const years = [...new Set([new Date().getFullYear(), ...resaYears])]
          const invs = await listInvoicesByEmitter({
            emitter_type: emitter.type,
            emitter_id: emitter.type === 'proprio' ? prop?.id : null,
            years,
          })
          if (mounted) setInvoicesByResa(indexerPiecesParResa(invs))
        } catch (e2) {
          if (mounted) console.warn('[recus] listInvoicesByEmitter :', e2.message)
        }
        if (!prefixError) {
          try {
            const { data: { session } } = await supabase.auth.getSession()
            const resp = await fetch('/api/beds24-mapping?action=getInvoiceSequences', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json',
                Authorization: `Bearer ${session?.access_token || ''}` },
              body: JSON.stringify({ action: 'getInvoiceSequences' }),
            })
            const j = await resp.json().catch(() => ({}))
            if (resp.ok && mounted) setSequences(j.sequences || [])
          } catch (e3) {
            if (mounted) console.warn('[recus] getInvoiceSequences :', e3.message)
          }
        }
        if (mounted) setLoading(false)
      })
    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log.id])

  const previewNumber = computePreviewNumber({
    prefix: invoicePrefix, year: new Date().getFullYear(), sequences })

  const voir = useCallback((r) => {
    setError(null); setInfo(null)
    const existing = invoicesByResa[r.id]
    // Faille N1 : copie archivée comme aperçu s'ouvrent dans un cadre isolé,
    // jamais écrites dans une page de l'appli (src/lib/apercu-isole.js). La
    // copie archivée est montrée telle quelle, jamais réécrite.
    if (existing && existing.html_snapshot) {
      if (!ouvrirApercuIsole(existing.html_snapshot)) { setError('Le popup a été bloqué — autorise les popups pour ce site.'); return }
      return
    }
    const { html } = genFactureResaHTML({
      reservation: r, log: effectiveLog, prop, cfg,
      mode: 'preview', prefix: invoicePrefix, previewNumber })
    if (!ouvrirApercuIsole(html)) { setError('Le popup a été bloqué — autorise les popups pour ce site.'); return }
  }, [invoicesByResa, effectiveLog, prop, cfg, invoicePrefix, previewNumber])

  // NOUVEAU (dessin Dimitri) : renvoyer l'email d'un reçu déjà émis — le
  // html_snapshot archivé repart tel quel, AUCUN nouveau numéro. Lot 3c-3 :
  // par la porte serveur (renvoyerPiece) — destinataire, objet du courriel,
  // simulation, anti-doublon et journal sont décidés par le serveur.
  const renvoyer = useCallback(async (r) => {
    setError(null); setInfo(null)
    const inv = invoicesByResa[r.id]
    if (!inv || !inv.id) return
    setBusyId(r.id)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Pas de session admin active')
      const out = await renvoyerPiece({ invoiceId: inv.id, token,
        confirmer: (phrase) => window.confirm(phrase) })
      if (out.annule) return
      if (out.ok) setInfo(out.message); else setError(out.message)
    } catch (e) {
      console.error('[recus] renvoi', e)
      setError(e.message || String(e))
    } finally { setBusyId(null) }
  }, [invoicesByResa])

  const sansRecu = resas.filter((r) => !invoicesByResa[r.id])
  return { resas, loading, busyId, info, error, invoicesByResa, sansRecu,
    previewNumber, prefixError, emitter, tabDocLabel, tabDocLabelLow,
    voir, renvoyer }
}

function VueRecus({ recus }) {
  const { resas, loading, busyId, info, error, invoicesByResa, sansRecu,
    previewNumber, prefixError, emitter } = recus
  const nbEmis = resas.length - sansRecu.length
  return (
    <>
      <div className="info">Ce sont des <b>reçus</b>, pas des factures : le
        paiement a eu lieu sur la plateforme. Les factures aux propriétaires
        vivent dans Argent.</div>
      {!emitter.adresse && (
        <div className="warn">Adresse {emitter.type === 'fbz' ? 'FBZ SAS' : 'du propriétaire'} vide —
          à renseigner ({emitter.type === 'fbz' ? 'Paramètres → Général' : 'la feuille Le propriétaire, onglet Infos'})
          pour la conformité du document.</div>
      )}
      {prefixError && (
        <div className="warn" data-testid="recus-prefixe-manquant">{prefixError}{' '}
          Aucune pièce ne sera émise au départ tant qu'il manque.</div>
      )}
      {error && (
        <div className="info" data-testid="recus-erreur"
          style={{ borderColor: '#e8c4b8', background: '#fbeee9',
            color: '#8a3b28' }}>{error}</div>
      )}
      {info && (
        <div className="info" data-testid="recus-info"
          style={{ borderColor: 'var(--vert-bd)', background: 'var(--vert-bg)',
            color: 'var(--vert-tx)' }}>{info}</div>
      )}
      <div className="carte" style={{ margin: '0 0 11px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px',
          flexWrap: 'wrap' }}>
          <span style={{ flex: '1', minWidth: '130px' }}>
            <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
              color: 'var(--navy-d)' }}>{resas.length} séjour{resas.length > 1 ? 's' : ''} sur ce logement</span>
            <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
              marginTop: '1px' }}>{nbEmis} reçu{nbEmis > 1 ? 's' : ''} déjà émis · {sansRecu.length} sans reçu</span>
          </span>
          <span style={{ display: 'flex', gap: '13px', flexShrink: '0' }}>
            <span style={{ textAlign: 'right' }}>
              <b style={{ display: 'block', fontSize: '15px', color: 'var(--navy-d)',
                lineHeight: '1' }}>{nbEmis}</b>
              <span style={{ display: 'block', fontSize: '9px', color: 'var(--gris-d)',
                marginTop: '2px' }}>émis</span>
            </span>
            <span style={{ textAlign: 'right' }}>
              <b style={{ display: 'block', fontSize: '15px', color: 'var(--navy-d)',
                lineHeight: '1' }}>{sansRecu.length}</b>
              <span style={{ display: 'block', fontSize: '9px', color: 'var(--gris-d)',
                marginTop: '2px' }}>sans reçu</span>
            </span>
          </span>
        </div>
        <p className="note">
          {previewNumber
            ? <>Le prochain numéro sera <b>{previewNumber}</b>. Un numéro attribué ne se reprend jamais.</>
            : 'Un numéro attribué ne se reprend jamais.'}</p>
      </div>
      {loading && (
        <p style={{ fontSize: '11.5px', color: 'var(--gris)' }}>Chargement…</p>
      )}
      {!loading && resas.length === 0 && (
        <div className="carte"><p style={{ fontSize: '11.5px', color: 'var(--gris)',
          margin: 0 }}>Aucune réservation V2 (Beds24) pour ce logement.</p></div>
      )}
      {resas.map((r) => {
        const inv = invoicesByResa[r.id]
        const nom = [r.guest_first_name, r.guest_last_name].filter(Boolean).join(' ') || '(sans nom)'
        const occupe = busyId === r.id
        return (
          <div key={r.id} className="carte" style={{ margin: '0 0 7px',
            padding: '10px 12px' }} data-testid={`recu-resa-${r.id}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
              flexWrap: 'wrap' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%',
                flexShrink: '0', background: inv ? '#2f9e6e' : '#c3cdd6' }} />
              <span style={{ flex: '1', minWidth: '120px' }}>
                <span style={{ display: 'block', fontSize: '12px', fontWeight: '600',
                  color: 'var(--navy-d)', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nom}</span>
                <span style={{ display: 'block', fontSize: '10px', color: 'var(--gris)',
                  marginTop: '1px' }}>
                  {fmtDateCourt(r.start_date)} → {fmtDateCourt(r.end_date)} · {SOURCES_LB[r.source] || r.source || '?'}</span>
              </span>
              <b style={{ fontSize: '12px', color: 'var(--navy-d)',
                flexShrink: '0' }}>{r.total_amount != null ? fmtEuros(r.total_amount) : '—'}</b>
              {inv && (
                // La pastille a l'air d'un lien : elle en est un (constat
                // Dimitri 2026-08-28 — elle n'ouvrait rien). Même document
                // archivé que « Voir ».
                <button className="tag t-v" style={{ flexShrink: '0',
                  border: 'none', cursor: 'pointer' }}
                  data-testid={`recu-numero-${r.id}`}
                  onClick={() => recus.voir(r)}
                  title={`${invoiceTypeLabel(inv.invoice_type)} émis le ${new Date(inv.sent_at || inv.created_at).toLocaleDateString('fr-FR')} — inviolable CGI 289. Ouvrir le document.`}>
                  {inv.invoice_number}</button>
              )}
              <span style={{ display: 'flex', gap: '5px', flexShrink: '0' }}>
                <button onClick={() => recus.voir(r)}
                  data-testid={`recu-voir-${r.id}`}
                  title={inv ? 'Le document archivé' : 'Aperçu — ne consomme aucun numéro'}
                  style={{ minHeight: '32px', padding: '0 10px', borderRadius: '8px',
                    border: '1px solid #d5e3ee', background: '#fff', fontSize: '10.5px',
                    color: 'var(--navy)', cursor: 'pointer' }}>Voir</button>
                {inv && (
                  <button onClick={() => recus.renvoyer(r)}
                    disabled={occupe || !r.guest_email}
                    data-testid={`recu-renvoyer-${r.id}`}
                    title={r.guest_email ? 'Renvoyer le même document par email' : "Pas d'email voyageur"}
                    style={{ minHeight: '32px', padding: '0 10px', borderRadius: '8px',
                      border: '1px solid #d5e3ee', background: '#fff', fontSize: '10.5px',
                      color: 'var(--gris)',
                      cursor: occupe || !r.guest_email ? 'not-allowed' : 'pointer',
                      opacity: occupe || !r.guest_email ? 0.55 : 1 }}>
                    {occupe ? '…' : 'Renvoyer'}</button>
                )}
              </span>
            </div>
          </div>
        )
      })}
      <p className="note">« Voir » montre le document sans consommer de numéro.
        La pièce est émise automatiquement au départ, une seule par séjour : son
        numéro est séquentiel et inviolable (CGI Art. 289), jamais de ré-émission.</p>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════
   LE FORMULAIRE — allure · champs · pièce d'identité · lien envoyé.
   ═══════════════════════════════════════════════════════════════════ */
const DESCRIPTIONS_REF = {
  email: 'pour lui envoyer son livret et son reçu',
  phone: "pour le joindre le jour de l'arrivée",
  tel: "pour le joindre le jour de l'arrivée",
  time_in: 'elle décide du créneau de ménage',
  time_out: 'elle décide du créneau de ménage',
  message: "il y écrit ce qu'il veut",
  billing_address: "utile s'il demande une facture",
}
const REFS_VITAUX = { email: 'sans lui, ni livret ni reçu ne partent',
  time_in: 'sans elle, le créneau de ménage se cale à l’aveugle',
  time_out: 'sans elle, le créneau de ménage se cale à l’aveugle' }
const DOC_FIELD_REFS = new Set(['id_type', 'passport_photo', 'id_recto', 'id_verso'])
const flecheChp = (active) => ({ width: '24px', height: '18px', borderRadius: '5px',
  border: '1px solid #d5e3ee', background: '#fff', fontSize: '8px',
  color: active ? 'var(--navy)' : '#dde4ea', cursor: active ? 'pointer' : 'default' })

function VueFormulaire({ draft, log, onGererLiens }) {
  const forms = log.forms || {}
  const unified = migrateLegacyToUnified(forms)
  const idDocPolicy = getIdDocPolicy(forms)
  const [feuilleChamp, setFeuilleChamp] = useState(null) // idx | 'nouveau'
  const [envoiCouverture, setEnvoiCouverture] = useState(false)
  const couverture = resolveCoverPhoto(unified)

  const saveUnified = (next) => draft.updateLog('forms', { ...forms, unified: next })
  const setIdDocChannel = (channel, enabled) =>
    draft.updateLog('forms', { ...forms, id_doc_policy: { ...idDocPolicy, [channel]: enabled } })
  const updateGlobal = (field, value) => saveUnified({ ...unified, [field]: value })

  const champs = (unified.fields || []).filter((f) => !DOC_FIELD_REFS.has(f.reference))
  const bougerChamp = (idx, delta) => {
    const tous = unified.fields || []
    const posDe = tous.indexOf(champs[idx])
    const posA = tous.indexOf(champs[idx + delta])
    if (posDe < 0 || posA < 0) return
    const next = [...tous]
    const [x] = next.splice(posDe, 1)
    next.splice(posA, 0, x)
    saveUnified({ ...unified, fields: next.map((f, i) => ({ ...f, order: i })) })
  }
  const retirerChamp = (champ) => {
    const vital = REFS_VITAUX[champ.reference]
    const msg = vital
      ? `Retirer « ${champ.label} » ? ${vital}. Rien ne vous en empêche — mais vous perdez ça.`
      : `Retirer « ${champ.label} » ?`
    if (!window.confirm(msg)) return
    saveUnified({ ...unified, fields: (unified.fields || []).filter((f) => f.id !== champ.id) })
  }

  const expiry = unified.link_expiry_days ?? 14

  return (
    <>
      <div className="info">Ce que vous demandez au voyageur <b>avant son
        arrivée</b>. Les heures qu'il donne ici décident du créneau de ménage.</div>
      <div className="cols2">
        <div>
          <p className="inter" style={{ marginTop: '0' }}>L'allure de la page</p>
          <div className="carte" style={{ margin: '0' }}>
            <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
              <span style={{ display: 'block', fontSize: '12px', color: 'var(--gris)',
                marginBottom: '6px' }}>Titre</span>
              <input value={unified.title || ''} data-testid="form-titre"
                onChange={(e) => updateGlobal('title', e.target.value)}
                placeholder="ex : Quelques informations avant votre arrivée"
                style={{ width: '100%', minHeight: '38px', padding: '0 12px',
                  borderRadius: '9px', background: '#f8fafc',
                  border: '1px solid #d5e3ee', fontSize: '12.5px', fontWeight: '600',
                  color: 'var(--navy-d)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
              <span style={{ display: 'block', fontSize: '12px', color: 'var(--gris)',
                marginBottom: '6px' }}>Texte d'introduction</span>
              <textarea value={unified.description || ''} rows={2}
                data-testid="form-description"
                onChange={(e) => updateGlobal('description', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '9px',
                  background: '#f8fafc', border: '1px solid #d5e3ee',
                  fontSize: '12px', color: 'var(--navy-d)', outline: 'none',
                  boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
              <span style={{ display: 'block', fontSize: '12px', color: 'var(--gris)',
                marginBottom: '6px' }}>Où l'envoyer après l'envoi</span>
              <input value={unified.redirect_url || ''} data-testid="form-redirect"
                onChange={(e) => updateGlobal('redirect_url', e.target.value)}
                placeholder="https://… (optionnel)"
                style={{ width: '100%', minHeight: '38px', padding: '0 12px',
                  borderRadius: '9px', background: '#f8fafc',
                  border: '1px solid #d5e3ee', fontSize: '12px',
                  color: 'var(--navy-d)', outline: 'none', boxSizing: 'border-box' }} />
              {log.livret_url && unified.redirect_url !== log.livret_url && (
                <button className="bt bt-s" style={{ marginTop: '6px',
                  minHeight: '30px', fontSize: '10.5px' }}
                  onClick={() => updateGlobal('redirect_url', log.livret_url)}>
                  Pré-remplir avec le livret d'accueil</button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
              padding: '9px 0' }}>
              <span style={{ flex: '1', minWidth: '0' }}>
                <span style={{ display: 'block', fontSize: '12px',
                  color: 'var(--gris)' }}>Photo de couverture</span>
                <span style={{ display: 'block', fontSize: '9.5px',
                  color: 'var(--gris-d)', marginTop: '1px' }}>
                  {envoiCouverture ? 'envoi en cours…' : couverture ? 'la vôtre' : 'aucune pour l’instant'}</span>
              </span>
              {couverture && (
                <img src={couverture} alt="" style={{ width: '52px',
                  height: '34px', borderRadius: '8px', objectFit: 'cover',
                  flexShrink: 0 }} />
              )}
              <label className="bt bt-s" style={{ minHeight: '34px',
                fontSize: '11px', cursor: 'pointer', flexShrink: 0 }}>
                {couverture ? 'Changer' : 'Ajouter'}
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    if (!file) return
                    // La photo part dans le STOCKAGE de fichiers ; seule son
                    // adresse entre dans la configuration (jamais un base64 :
                    // c'est ce qui a coupé la base le 2026-09-14).
                    setEnvoiCouverture(true)
                    try {
                      const patch = await uploadCoverPhoto(log.id, file)
                      saveUnified({ ...unified, ...patch })
                    } catch (err) {
                      console.error('[FicheDocsCtrl] couverture', err)
                      window.alert(err.message || 'Téléversement échoué')
                    } finally {
                      setEnvoiCouverture(false)
                    }
                  }} /></label>
              {couverture && (
                <button className="bt bt-s" style={{ minHeight: '34px',
                  fontSize: '11px', color: '#b8412b', borderColor: '#e8c4b8',
                  flexShrink: 0 }}
                  onClick={() => saveUnified({ ...unified, ...clearCoverPhotoPatch() })}>✕</button>
              )}
            </div>
          </div>

          <p className="inter">Les champs</p>
          <div className="carte" style={{ margin: '0' }}>
            {champs.length === 0 && (
              <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: 0 }}>
                Aucun champ — le voyageur n'aurait rien à remplir.</p>
            )}
            {champs.map((f, idx) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center',
                gap: '9px', padding: '9px 0',
                borderTop: idx > 0 ? '1px solid var(--bord)' : 'none' }}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '1px',
                  flexShrink: '0' }}>
                  <button onClick={() => bougerChamp(idx, -1)} disabled={idx === 0}
                    aria-label="Monter le champ" style={flecheChp(idx > 0)}>▲</button>
                  <button onClick={() => bougerChamp(idx, 1)}
                    disabled={idx === champs.length - 1}
                    aria-label="Descendre le champ"
                    style={flecheChp(idx < champs.length - 1)}>▼</button>
                </span>
                <button onClick={() => setFeuilleChamp((unified.fields || []).indexOf(f))}
                  data-testid={`form-champ-${f.id}`}
                  style={{ flex: '1', minWidth: '0', textAlign: 'left',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <span style={{ display: 'block', fontSize: '12px', fontWeight: '600',
                    color: 'var(--navy-d)' }}>{f.label || '(sans nom)'}</span>
                  <span style={{ display: 'block', fontSize: '10px',
                    color: 'var(--gris)', marginTop: '1px' }}>
                    {DESCRIPTIONS_REF[f.reference] || f.type || 'champ libre'}</span>
                </button>
                <button onClick={() => updateChampRequired(f)}
                  data-testid={`form-exige-${f.id}`}
                  style={f.required
                    ? { minHeight: '32px', padding: '0 10px', borderRadius: '8px',
                      fontSize: '10.5px', flexShrink: '0', background: 'var(--vert-bg)',
                      border: '1px solid var(--vert-bd)', color: 'var(--vert-tx)',
                      fontWeight: '600', cursor: 'pointer' }
                    : { minHeight: '32px', padding: '0 10px', borderRadius: '8px',
                      fontSize: '10.5px', flexShrink: '0', background: '#fff',
                      border: '1px solid #d5e3ee', color: 'var(--gris)',
                      cursor: 'pointer' }}>
                  {f.required ? 'exigé' : 'facultatif'}</button>
                <button onClick={() => retirerChamp(f)}
                  aria-label={`Retirer ${f.label}`}
                  style={{ width: '26px', height: '26px', borderRadius: '7px',
                    background: 'none', border: 'none', color: 'var(--gris-d)',
                    fontSize: '12px', flexShrink: '0', cursor: 'pointer' }}>✕</button>
              </div>
            ))}
          </div>
          <div className="act">
            <button className="bt bt-s" data-testid="form-nouveau-champ"
              onClick={() => setFeuilleChamp('nouveau')}>＋ Un champ</button>
          </div>
          <p className="note">Tout se retire. Trois champs font marcher le reste
            de l'application — les retirer vous dira ce que vous perdez, sans
            vous en empêcher.</p>
          <div className="dang">« Exigé » n'engage que le navigateur : une
            soumission fabriquée passerait sans ces champs. C'est une contrainte
            de saisie, pas une garantie.</div>

          <p className="inter">La pièce d'identité</p>
          <div className="carte" style={{ margin: '0' }}>
            <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: '0 0 10px',
              lineHeight: '1.6' }}>Trois règles s'empilent :{' '}
              <b style={{ color: 'var(--navy-d)' }}>le canal décide si</b> on la
              demande, <b style={{ color: 'var(--navy-d)' }}>le type décide quoi</b>{' '}
              s'affiche, <b style={{ color: 'var(--navy-d)' }}>la case décide si</b>{' '}
              c'est exigé.</p>
            {[{ key: 'direct', lb: 'Réservations directes', note: null },
              { key: 'booking', lb: 'Booking', note: null },
              { key: 'airbnb', lb: 'Airbnb', note: "Airbnb vérifie déjà l'identité" }].map((ch) => {
              const on = idDocPolicy[ch.key] !== false
              return (
                <div key={ch.key} style={{ display: 'flex', alignItems: 'center',
                  gap: '10px', padding: '9px 0', borderTop: '1px solid var(--bord)' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%',
                    flexShrink: '0', background: on ? '#2f9e6e' : '#c3cdd6' }} />
                  <span style={{ flex: '1', minWidth: '0' }}>
                    <span style={{ display: 'block', fontSize: '12px',
                      color: 'var(--navy-d)' }}>{ch.lb}</span>
                    {ch.note && (
                      <span style={{ display: 'block', fontSize: '10px',
                        color: 'var(--gris)', marginTop: '1px' }}>{ch.note}</span>
                    )}
                  </span>
                  <ToggleRond on={on} testId={`form-iddoc-${ch.key}`}
                    ariaLabel={`Demander la pièce d'identité — ${ch.lb}`}
                    onClick={() => setIdDocChannel(ch.key, !on)} />
                </div>
              )
            })}
            <div style={{ paddingTop: '10px', marginTop: '6px',
              borderTop: '1px solid var(--bord)' }}>
              <span style={{ display: 'block', fontSize: '11.5px', fontWeight: '600',
                color: 'var(--navy-d)', marginBottom: '6px' }}>Ce que le voyageur
                voit selon son choix</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                padding: '6px 0' }}>
                <span style={{ flex: '1', fontSize: '11.5px',
                  color: 'var(--navy-d)' }}>Passeport</span>
                <span style={{ fontSize: '10.5px', color: 'var(--gris)' }}>la page
                  d'identité, seule</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                padding: '6px 0', borderTop: '1px solid var(--bord)' }}>
                <span style={{ flex: '1', fontSize: '11.5px',
                  color: 'var(--navy-d)' }}>Carte d'identité</span>
                <span style={{ fontSize: '10.5px', color: 'var(--gris)' }}>le recto
                  et le verso</span>
              </div>
              <p className="note">Il ne voit jamais les trois champs en même
                temps. Les pièces déposées ne sont visibles que de vous.</p>
            </div>
          </div>

          <p className="inter">Le lien envoyé</p>
          <div className="carte" style={{ margin: '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ flex: '1', minWidth: '0' }}>
                <span style={{ display: 'block', fontSize: '12px',
                  fontWeight: '600' }}>Un lien par réservation</span>
                <span style={{ display: 'block', fontSize: '10px',
                  color: 'var(--gris)', marginTop: '1px' }}>valable {expiry} jour{expiry > 1 ? 's' : ''},
                  puis il expire</span>
              </span>
              <button aria-label="Diminuer la durée" data-testid="form-expiry-moins"
                onClick={() => updateGlobal('link_expiry_days', Math.max(1, expiry - 1))}
                style={{ width: '32px', height: '32px', borderRadius: '8px',
                  border: '1px solid #c3d5e4', background: '#fff', fontSize: '16px',
                  color: 'var(--navy)', flexShrink: '0', cursor: 'pointer' }}>−</button>
              <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '14px',
                fontWeight: '600' }}>{expiry}</span>
              <button aria-label="Augmenter la durée" data-testid="form-expiry-plus"
                onClick={() => updateGlobal('link_expiry_days', Math.min(365, expiry + 1))}
                style={{ width: '32px', height: '32px', borderRadius: '8px',
                  border: '1px solid #c3d5e4', background: '#fff', fontSize: '16px',
                  color: 'var(--navy)', flexShrink: '0', cursor: 'pointer' }}>+</button>
            </div>
            <p className="note">Un lien déjà envoyé ne casse jamais, même si vous
              changez ce réglage.</p>
            <div className="act">
              <button className="bt bt-s" data-testid="form-gerer-liens"
                onClick={onGererLiens}>Gérer les liens…</button>
            </div>
          </div>
        </div>
        <div className="surOrdi">
          <div style={{ position: 'sticky', top: '14px' }}>
            <p className="inter" style={{ marginTop: '0' }}>Ce que reçoit le voyageur</p>
            <ApercuFormVoyageur log={log} unified={unified} idDocPolicy={idDocPolicy} />
          </div>
        </div>
      </div>

      {feuilleChamp !== null && (
        <FeuilleChamp
          champ={feuilleChamp === 'nouveau' ? null : (unified.fields || [])[feuilleChamp]}
          onEnregistrer={(patch) => {
            if (feuilleChamp === 'nouveau') {
              saveUnified({ ...unified, fields: [...(unified.fields || []),
                { id: `fld_${Date.now().toString(36)}`, type: 'input',
                  input_type: 'text', required: false, reference: '', ...patch }] })
            } else {
              saveUnified({ ...unified,
                fields: (unified.fields || []).map((f, j) => (j === feuilleChamp ? { ...f, ...patch } : f)) })
            }
            setFeuilleChamp(null)
          }}
          onFermer={() => setFeuilleChamp(null)} />
      )}
    </>
  )

  function updateChampRequired(f) {
    saveUnified({ ...unified,
      fields: (unified.fields || []).map((x) => (x.id === f.id ? { ...x, required: !x.required } : x)) })
  }
}

/* ── l'aperçu voyageur (neutralisé — contenu, pas dessin) ─────────── */
function ApercuFormVoyageur({ log, unified, idDocPolicy }) {
  const [canal, setCanal] = useState('direct')
  const idDemandee = idDocPolicy[canal] !== false
  const champs = (unified.fields || []).filter((f) => !DOC_FIELD_REFS.has(f.reference))
  return (
    <>
      <div style={{ display: 'flex', gap: '9px', background: '#eef2f6',
        border: '1px dashed #c3cdd6', borderRadius: '12px', padding: '10px 11px',
        marginBottom: '10px' }}>
        <span style={{ width: '22px', height: '22px', borderRadius: '6px',
          background: '#fff', border: '1px solid var(--bord)', color: 'var(--gris)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', flexShrink: '0' }}>↻</span>
        <span style={{ flex: '1', minWidth: '0', fontSize: '10.5px',
          color: 'var(--gris-d)', lineHeight: '1.5' }}>La page du voyageur reste{' '}
          <b>telle qu'elle est aujourd'hui</b>. Ce qui suit en montre le contenu,
          pas le dessin.</span>
      </div>
      <div style={{ display: 'flex', gap: '4px', background: '#eef2f6',
        borderRadius: '10px', padding: '3px', marginBottom: '10px' }}>
        {[['direct', 'Direct'], ['booking', 'Booking'], ['airbnb', 'Airbnb']].map(([id, lb]) => (
          <button key={id} onClick={() => setCanal(id)}
            data-testid={`form-apercu-${id}`}
            style={canal === id
              ? { flex: '1', minHeight: '34px', borderRadius: '8px', fontSize: '11px',
                background: '#fff', color: 'var(--navy-d)', fontWeight: '600',
                boxShadow: '0 1px 3px rgba(35,50,64,.12)', border: 'none',
                cursor: 'pointer' }
              : { flex: '1', minHeight: '34px', borderRadius: '8px', fontSize: '11px',
                color: 'var(--gris)', background: 'transparent', border: 'none',
                cursor: 'pointer' }}>{lb}</button>
        ))}
      </div>
      <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
        <div style={{ height: '66px',
          background: 'linear-gradient(135deg,#c3d5e4,#87b8e6)', display: 'flex',
          alignItems: 'flex-end', padding: '10px 13px', position: 'relative',
          overflow: 'hidden' }}>
          {resolveCoverPhoto(unified) && (
            <img src={resolveCoverPhoto(unified)} alt="" style={{ position: 'absolute',
              inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          <span style={{ position: 'relative', fontSize: '13px', fontWeight: '600',
            color: '#fff' }}>{log.nom_complet || log.nom || log.id}</span>
        </div>
        <div style={{ padding: '13px' }}>
          {(unified.title || unified.description) && (
            <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: '0 0 12px',
              lineHeight: '1.6' }}>
              {[unified.title, unified.description].filter(Boolean).join(', ')}.</p>
          )}
          {champs.map((f) => (
            <div key={f.id} style={{ marginBottom: '9px' }}>
              <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
                marginBottom: '4px' }}>{f.label}
                {f.required && <span style={{ color: 'var(--terra)' }}> *</span>}
              </span>
              <div style={{ border: '1px solid #d5e3ee', borderRadius: '9px',
                padding: '9px 11px', fontSize: '11.5px', color: 'var(--gris-d)',
                minHeight: '36px', boxSizing: 'border-box' }}>
                {['time', 'dropdown'].includes(f.type) || ['time_in', 'time_out'].includes(f.reference)
                  ? '— choisir un créneau —' : ''}
              </div>
            </div>
          ))}
          {idDemandee && (
            <div style={{ marginBottom: '9px', paddingTop: '9px',
              borderTop: '1px solid var(--bord)' }}>
              <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
                marginBottom: '6px' }}>Pièce d'identité<span style={{ color: 'var(--terra)' }}> *</span>
              </span>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '7px' }}>
                <span style={{ flex: '1', textAlign: 'center', padding: '8px 4px',
                  borderRadius: '9px', fontSize: '10.5px', background: 'var(--ciel-p)',
                  border: '1px solid var(--navy)', color: 'var(--navy-d)',
                  fontWeight: '600' }}>Passeport</span>
                <span style={{ flex: '1', textAlign: 'center', padding: '8px 4px',
                  borderRadius: '9px', fontSize: '10.5px', background: '#fff',
                  border: '1px solid #d5e3ee', color: 'var(--gris)' }}>Carte d'identité</span>
              </div>
              <div style={{ border: '1px dashed #c3d5e4', borderRadius: '9px',
                padding: '14px', textAlign: 'center', fontSize: '10.5px',
                color: 'var(--gris)' }}>Page d'identité du passeport</div>
            </div>
          )}
          <div style={{ background: 'var(--ciel-p)', borderRadius: '10px',
            padding: '10px 11px', marginBottom: '12px' }}>
            <p style={{ fontSize: '10.5px', margin: '0', color: 'var(--navy-d)',
              lineHeight: '1.5' }}>
              <b>Vos données sont protégées.</b> Votre pièce d'identité n'est
              visible que de nous, et supprimée après votre séjour.</p>
          </div>
          <div style={{ background: 'var(--terra)', borderRadius: '10px',
            padding: '11px', textAlign: 'center', fontSize: '12px', color: '#fff',
            fontWeight: '600' }}>Envoyer</div>
        </div>
      </div>
      <p className="note">Les créneaux réels dépendent du ménage prévu ce
        jour-là : ils n'apparaissent qu'au voyageur.</p>
    </>
  )
}

/* ── la feuille d'un champ (label · type · référence · options) ───── */
const FIELD_TYPES = [
  { value: 'input', label: 'Saisie libre' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'dropdown', label: 'Liste déroulante' },
  { value: 'radio', label: 'Choix unique' },
  { value: 'checkbox', label: 'Cases à cocher' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Heure' },
  { value: 'file', label: 'Fichier' },
]
const INPUT_TYPES = [
  { value: 'text', label: 'Texte' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Téléphone' },
  { value: 'number', label: 'Nombre' },
]

function FeuilleChamp({ champ, onEnregistrer, onFermer }) {
  const [label, setLabel] = useState(champ?.label || 'Nouveau champ')
  const [type, setType] = useState(champ?.type || 'input')
  const [inputType, setInputType] = useState(champ?.input_type || 'text')
  const [reference, setReference] = useState(champ?.reference || '')
  const [required, setRequired] = useState(!!champ?.required)
  const [options, setOptions] = useState((champ?.options || []).join('\n'))

  const ligne = (lb, enfant) => (
    <div style={{ marginBottom: '9px' }}>
      <span style={{ display: 'block', fontSize: '11px', color: 'var(--gris)',
        marginBottom: '5px' }}>{lb}</span>
      {enfant}
    </div>
  )
  const inp = { width: '100%', minHeight: '38px', padding: '0 12px',
    borderRadius: '9px', background: '#f8fafc', border: '1px solid #d5e3ee',
    fontSize: '12.5px', color: 'var(--navy-d)', outline: 'none',
    boxSizing: 'border-box' }

  return (
    <Feuille titre={champ ? `Le champ « ${champ.label} »` : 'Nouveau champ'}
      testId="feuille-champ" onFermer={onFermer}
      enfants={(
        <>
          {ligne('Ce que lit le voyageur', (
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              data-testid="champ-label" style={inp} />
          ))}
          {ligne('Type de champ', (
            <select value={type} onChange={(e) => setType(e.target.value)}
              style={inp}>
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          ))}
          {type === 'input' && ligne('Type de saisie', (
            <select value={inputType} onChange={(e) => setInputType(e.target.value)}
              style={inp}>
              {INPUT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          ))}
          {ligne('Référence (clé technique du payload)', (
            <input value={reference} data-testid="champ-reference"
              onChange={(e) => setReference(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="ex : email" style={inp} />
          ))}
          {['dropdown', 'radio', 'checkbox'].includes(type) && ligne('Options (une par ligne)', (
            <textarea value={options} rows={4}
              onChange={(e) => setOptions(e.target.value)}
              style={{ ...inp, minHeight: '80px', padding: '9px 12px',
                resize: 'vertical' }} />
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px',
            fontSize: '12px', color: 'var(--navy-d)', marginBottom: '10px',
            cursor: 'pointer' }}>
            <input type="checkbox" checked={required}
              onChange={(e) => setRequired(e.target.checked)} />
            Champ exigé (contrainte de saisie navigateur)
          </label>
          <p className="note">Champs spéciaux : time_in / time_out (bornés par les
            horaires du logement) · id_type (Passeport → photo seule ; Carte →
            recto + verso) · les dates de séjour se pré-remplissent depuis la
            réservation du lien.</p>
          <div className="act">
            <button className="bt bt-p" data-testid="champ-enregistrer"
              onClick={() => onEnregistrer({ label, type, input_type: inputType,
                reference, required,
                options: options.split('\n').map((s) => s.trim()).filter(Boolean) })}>
              Enregistrer</button>
            <button className="bt bt-s" onClick={onFermer}>Annuler</button>
          </div>
        </>
      )} />
  )
}

/* ── la feuille « Gérer les liens » (tokens publics conservés) ────── */
export function FeuilleLiens({ logementId, expiryDays = 14, onFermer }) {
  const [tokens, setTokens] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [info, setInfo] = useState(null)
  const [avecHistorique, setAvecHistorique] = useState(false)
  const [enCours, setEnCours] = useState(false)

  const recharger = useCallback(async () => {
    try {
      const all = await listFormTokens(logementId)
      setTokens(all || [])
    } catch (e) { setErreur(e.message || String(e)) }
  }, [logementId])
  useEffect(() => { recharger() }, [recharger])

  const estPerime = (tk) => tk.expires_at && new Date(tk.expires_at) < new Date()
  const actifs = (tokens || []).filter((tk) => !estPerime(tk) && !tk.used_at)
  const historiques = (tokens || []).filter((tk) => estPerime(tk) || tk.used_at)
  const visibles = avecHistorique ? (tokens || []) : actifs

  return (
    <Feuille titre="Les liens du formulaire" testId="feuille-form-liens" large
      onFermer={onFermer}
      enfants={(
        <>
          <div className="info">Un lien = une réservation, valable {expiryDays}{' '}
            jour{expiryDays > 1 ? 's' : ''} (le réglage de la carte « Le lien
            envoyé »). Générer le copie aussitôt, prêt à coller dans un message.</div>
          {erreur && (
            <div className="info" style={{ borderColor: '#e8c4b8',
              background: '#fbeee9', color: '#8a3b28' }}>{erreur}</div>
          )}
          {info && (
            <div className="info" style={{ borderColor: 'var(--vert-bd)',
              background: 'var(--vert-bg)', color: 'var(--vert-tx)' }}>{info}</div>
          )}
          <div className="act" style={{ marginTop: 0 }}>
            <button className="bt bt-p" data-testid="liens-generer" disabled={enCours}
              onClick={async () => {
                setEnCours(true); setErreur(null); setInfo(null)
                try {
                  const tk = await createFormToken({ type: 'unified',
                    logement_id: logementId, expires_in_days: expiryDays })
                  const url = publicFormUrl(tk.token)
                  try {
                    await navigator.clipboard.writeText(url)
                    setInfo(`Lien généré et copié : ${url}`)
                  } catch {
                    setInfo(`Lien généré : ${url}`)
                  }
                  await recharger()
                } catch (e) { setErreur(e.message || String(e)) } finally { setEnCours(false) }
              }}>Générer un lien</button>
            {historiques.length > 0 && (
              <button className="bt bt-s"
                onClick={() => setAvecHistorique((v) => !v)}>
                {avecHistorique ? 'Masquer' : 'Afficher'} l'historique ({historiques.length})</button>
            )}
          </div>
          {tokens === null && (
            <p style={{ fontSize: '11.5px', color: 'var(--gris)' }}>Chargement…</p>
          )}
          {tokens !== null && visibles.length === 0 && (
            <p style={{ fontSize: '11.5px', color: 'var(--gris)' }}>Aucun lien
              {avecHistorique ? '' : ' actif'} pour l'instant.</p>
          )}
          {visibles.map((tk) => {
            const perime = estPerime(tk)
            const url = publicFormUrl(tk.token)
            return (
              <div key={tk.token} className="carte" style={{ margin: '0 0 7px',
                padding: '9px 12px', opacity: perime ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                  flexWrap: 'wrap' }}>
                  <span style={{ flex: '1', minWidth: '140px' }}>
                    <span style={{ display: 'block', fontSize: '11px',
                      color: 'var(--navy-d)', fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }}>{url}</span>
                    <span style={{ display: 'block', fontSize: '9.5px',
                      color: 'var(--gris-d)', marginTop: '1px' }}>
                      créé le {tk.created_at ? new Date(tk.created_at).toLocaleDateString('fr-FR') : '—'}
                      {tk.expires_at ? ` · ${perime ? 'expiré' : 'expire'} le ${new Date(tk.expires_at).toLocaleDateString('fr-FR')}` : ''}
                      {tk.used_at ? ' · utilisé' : ''}</span>
                  </span>
                  <span style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                    <button className="bt bt-s" disabled={perime}
                      style={{ minHeight: '30px', fontSize: '10.5px' }}
                      onClick={() => window.open(url, '_blank', 'noopener')}>Voir</button>
                    <button className="bt bt-s" disabled={perime}
                      style={{ minHeight: '30px', fontSize: '10.5px' }}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(url)
                          setInfo('Lien copié.')
                        } catch { setErreur('Copie impossible.') }
                      }}>Copier</button>
                    <button className="bt bt-s"
                      style={{ minHeight: '30px', fontSize: '10.5px',
                        color: '#b8412b', borderColor: '#e8c4b8' }}
                      onClick={async () => {
                        if (!window.confirm('Révoquer ce lien ? La révocation est définitive.')) return
                        try {
                          await deleteFormToken(tk.token)
                          await recharger()
                        } catch (e) { setErreur(e.message || String(e)) }
                      }}>Révoquer</button>
                  </span>
                </div>
              </div>
            )
          })}
          <div className="act">
            <button className="bt bt-s" onClick={onFermer}>Fermer</button>
          </div>
        </>
      )} />
  )
}
